<template>
  <div v-if="salesDocument" class="grid h-full grid-cols-1 grid-rows-[auto_1fr]">
    <!-- Header -->
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      link-name="sales-documents"
      :is-modified="isModified"
      :mode="mode"
      :is-edit-active="isDocumentEditable"
      @edit-mode="mode = 'edit'"
      @cancel="onCancelEdit"
      @save="onSaveChanges"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)" />

    <AppResizableContainer
      :layout="isHorizontalLayout ? 'vertical' : 'horizontal'"
      :minSize="150"
      class="overflow-auto">
      <template #headerTitle v-if="isHorizontalLayout">
        <nav
          role="section-nav"
          class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
          <ul class="flex flex-wrap items-center gap-4 pl-4 -mb-px">
            <li
              v-for="tab in headerTitleTabs"
              :key="tab.name"
              @click="selectedHeaderTitleTab = tab.name"
              class="py-1.5 cursor-pointer"
              :class="selectedHeaderTitleTab === tab.name ? 'border-b-2 border-theme-500' : ''">
              <p>
                {{ tab.label }}
              </p>
            </li>
          </ul>
          <div class="py-1.5 px-4 flex items-center justify-start gap-2">
            <BaseControlsButtonIcon :tooltip-text="$t('common.tooltip.sendEmail')">
              <IconEnvelope class="size-4" />
            </BaseControlsButtonIcon>
            <BaseControlsButtonIcon
              :tooltip-text="$t('common.tooltip.print')"
              @button-clicked="previewModalOpen = true">
              <IconPrinter class="size-4" />
            </BaseControlsButtonIcon>
            <BaseControlsButtonIcon
              :tooltip-text="$t('common.tooltip.report')"
              :disabled="mode === 'edit'"
              @button-clicked="openReportTemplate">
              <IconTemplate class="size-4" />
            </BaseControlsButtonIcon>
          </div>
        </nav>
      </template>
      <template #headerContent>
        <SalesDocumentsHeader v-if="modifiedSalesDocument" :mode="mode" />
      </template>

      <!-- Tables Section -->
      <template #detailsTitle>
        <nav
          role="section-nav"
          class="flex justify-between px-3 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
          <ul class="flex flex-wrap items-center gap-4 px-4 py-3 -mb-px">
            <template v-for="tab in detailsTitleTabs" :key="tab.name">
              <li
                v-if="tab.link"
                @click="selectedDetailsTitleTab = tab.name"
                class="cursor-pointer"
                :class="isActiveTab(tab) ? 'border-b-2 border-theme-500' : ''">
                <p>{{ tab.label }}</p>
              </li>
            </template>
          </ul>
          <div
            v-if="selectedDetailsTitleTab.includes('materials')"
            class="py-1.5 px-4 flex items-center justify-start gap-2">
            <BaseControlsButtonIcon
              @button-clicked="selectedDetailsTitleTab = 'materialsTable'"
              :tooltip-text="$t('common.tooltip.tableView')"
              :active="selectedDetailsTitleTab == 'materialsTable'">
              <IconLayoutSections class="size-4" />
            </BaseControlsButtonIcon>
            <BaseControlsButtonIcon
              @button-clicked="selectedDetailsTitleTab = 'materialsEnhanced'"
              :tooltip-text="$t('common.tooltip.enhancedView')"
              :active="selectedDetailsTitleTab == 'materialsEnhanced'">
              <IconLayoutPictureRight class="size-4" />
            </BaseControlsButtonIcon>
            <BaseControlsButtonIcon
              @button-clicked="selectedDetailsTitleTab = 'materialsCard'"
              :tooltip-text="$t('common.tooltip.cardView')"
              :active="selectedDetailsTitleTab == 'materialsCard'">
              <IconLayoutContentLeft class="size-4" />
            </BaseControlsButtonIcon>
          </div>
        </nav>
      </template>

      <template #detailsContent>
        <SalesDocumentsDetailsTable
          v-if="selectedDetailsTitleTab == 'materialsTable'"
          @material-info="openMaterialInfoModal"
          @validate-header="$v.$touch()"
          :mode="mode" />
        <SalesDocumentsDetailsEnhanced
          v-if="selectedDetailsTitleTab == 'materialsEnhanced'"
          :isHorizontalLayout="isHorizontalLayout"
          @material-info="openMaterialInfoModal"
          :mode="mode" />
        <SalesDocumentsDetailsCard
          v-if="selectedDetailsTitleTab == 'materialsCard'"
          :isHorizontalLayout="isHorizontalLayout"
          @material-info="openMaterialInfoModal"
          :mode="mode" />
        <SalesDocumentsTaxes
          v-if="selectedDetailsTitleTab == 'taxes'"
          :is-horizontal-layout="isHorizontalLayout"
          :mode="mode" />
        <SalesDocumentsTotals v-if="selectedDetailsTitleTab == 'totals'" />
        <SalesDocumentsRemarks
          v-if="selectedDetailsTitleTab == 'remarks'"
          :mode="mode"
          :is-horizontal-layout="isHorizontalLayout" />
      </template>
    </AppResizableContainer>
    <SalesDocumentTotalsEdit :mode="mode" />
    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="confirmModalActive = false"
      @confirm="onDiscardChanges" />

    <InfoModal
      v-if="showMaterialRecentTransactionsModal"
      :title="$t('salesDocuments.recentTransactions')"
      @primaryButtonClicked="
        {
          showMaterialRecentTransactionsModal = false;
          selectedMaterial = undefined;
        }
      ">
      <template #content>
        <div class="w-full p-1.5 m-2 ml-0 rounded-md bg-cyan-light text-theme-600">
          {{ selectedMaterial?.code }} - {{ selectedMaterial?.name }}
        </div>
        <div
          v-if="materialRecentTransactions"
          v-html="materialRecentTransactions"
          class="overflow-y-auto max-h-[40vh]"></div>
        <div v-else>{{ $t('messages.errors.missingMaterialRecentTransactions') }}</div>
      </template>
    </InfoModal>
    <SalesDocumentPreview v-if="previewModalOpen" @close="previewModalOpen = false" />

    <SalesDocumentReportPreview
      v-if="
        reportPreviewModalOpen &&
        modifiedSalesDocument &&
        modifiedSalesDocument.documentHeader.documentType?.defaultReportingTemplateId
      "
      :sale-document="modifiedSalesDocument"
      :default-reporting-template-id="
        modifiedSalesDocument.documentHeader.documentType?.defaultReportingTemplateId
      "
      @close="reportPreviewModalOpen = false" />

    <InfoModal
      v-if="showMissingDetailsModal"
      @primaryButtonClicked="showMissingDetailsModal = false">
      <template #content>
        <div>{{ $t('salesDocuments.missingDocumentLines') }}</div>
      </template>
    </InfoModal>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useSalesDocumentsStore } from '@/stores/salesDocuments';
import { useLookupsStore } from '@/stores/lookups';
import { useToastStore } from '@/stores/toast';
import { addOrEditSalesDocument, getSalesDocumentById } from '@/services/api/documents';
import { getMaterialRecentTransactions } from '@/services/api/documents';
import { checkAndCreateRangeCustomerTradersite } from '@/services/api/ranges';
import { deepCopy } from '@/utils/data';
import { isoDateToDatepickerFormat } from '@/utils/date';
import useVuelidate from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import SalesDocumentsHeader from '@/components/transactions/salesDocuments/SalesDocumentsHeader.vue';
import SalesDocumentsDetailsTable from '@/components/transactions/salesDocuments/SalesDocumentsDetailsTable.vue';
import SalesDocumentsDetailsEnhanced from '@/components/transactions/salesDocuments/SalesDocumentsDetailsEnhanced.vue';
import SalesDocumentsDetailsCard from '@/components/transactions/salesDocuments/SalesDocumentsDetailsCard.vue';
import SalesDocumentsTaxes from '@/components/transactions/salesDocuments/SalesDocumentsTaxes.vue';
import SalesDocumentsTotals from '@/components/transactions/salesDocuments/SalesDocumentsTotals.vue';
import SalesDocumentsRemarks from '@/components/transactions/salesDocuments/SalesDocumentsRemarks.vue';
import IconLayoutContentLeft from '../../assets/icons/IconLayoutContentLeft.vue';
import BaseControlsButtonIcon from '@/components/common/BaseControlsButtonIcon.vue';
import IconLayoutPictureRight from '@/assets/icons/IconLayoutPictureRight.vue';
import IconLayoutSections from '@/assets/icons/IconLayoutSections.vue';
import IconEnvelope from '@/assets/icons/IconEnvelope.vue';
import IconPrinter from '@/assets/icons/IconPrinter.vue';
import InfoModal from '@/components/common/InfoModal.vue';
import SalesDocumentTotalsEdit from '@/components/transactions/salesDocuments/SalesDocumentTotalsEdit.vue';
import SalesDocumentReportPreview from '@/components/transactions/salesDocuments/SalesDocumentReportPreview.vue';
import IconTemplate from '@/assets/icons/IconTemplate.vue';
import { type SalesDocument, eDiscountOrigin } from '@/types/Documents';
import SalesDocumentPreview from '@/components/transactions/salesDocuments/SalesDocumentPreview.vue';
import type { Material } from '@/types/Materials';
import { initializeDecimalPlaces } from '@/utils/prices';
import { RecordMode } from '@/types/Base';
import { useAuthStore } from '@/stores/auth';
import { Users } from '@/utils/constants';

const { isAdmin, username } = storeToRefs(useAuthStore());
const route = useRoute();
const { t: $t } = useI18n({ useScope: 'global' });
const mode = ref<'view' | 'edit'>('view');

const isHorizontalLayout = ref(true);

const {
  updateHeaderTotals,
  searchMaterialAlterCodes,
  processSiblingMaterialAlterCodes,
  priceOrQuantityChange,
  saveDocumentLineInStore,
  initializeDynamicQuantities,
} = useSalesDocumentsStore();
const {
  salesDocument,
  modifiedSalesDocument,
  isModified,
  clearForm,
  clearDetails,
  docDetailLines,
  hasModifiedDetails,
} = storeToRefs(useSalesDocumentsStore());

const {
  getDocumentStatusesLookup,
  getDocumentTypesLookup,
  getSalesPersonsLookup,
  getDocumentSeriesLookup,
  getWarehousesLookup,
  getPaymentTypesLookup,
  getDeliveryTypesLookup,
  getDivisionsLookup,
  getCompaniesLookup,
  getCompanySitesLookup,
} = useLookupsStore();
const { dropdownCompanies } = storeToRefs(useLookupsStore());

const previewModalOpen = ref(false);
const reportPreviewModalOpen = ref(false);
onMounted(async () => {
  initializeDecimalPlaces();

  salesDocument.value = undefined;
  modifiedSalesDocument.value = undefined;

  await getDocumentStatusesLookup();
  await getDocumentTypesLookup();
  await getSalesPersonsLookup();
  await getDocumentSeriesLookup();
  await getWarehousesLookup();
  await getPaymentTypesLookup();
  await getDeliveryTypesLookup();
  await getDivisionsLookup();
  await getCompaniesLookup();
  await getCompanySitesLookup();
  await loadData();
  if (salesDocument.value)
    modifiedSalesDocument.value = deepCopy<SalesDocument>(salesDocument.value);
});

async function loadData() {
  const res = await getSalesDocumentById(route.params.id as string);
  if (res) {
    mode.value = 'view';
    if (res.documentHeader.officialDate) {
      res.documentHeader.officialDate = isoDateToDatepickerFormat(res.documentHeader.officialDate);
    }
    if (res.documentHeader.deliveryDate) {
      res.documentHeader.deliveryDate = isoDateToDatepickerFormat(res.documentHeader.deliveryDate);
    }
    salesDocument.value = res;
  } else {
    mode.value = 'edit';
    const compId = dropdownCompanies.value?.[0].value.toString();
    salesDocument.value = {
      id: route.params.id as string,
      createdDate: new Date().toISOString(),
      isActive: true,
      isFromManager: true,
      deliveryType: {},
      deleted: false,
      customerId: '',
      documentHeader: {
        id: uuidv4(),
        isFromManager: true,
        isCanceled: false,
        totalGross: 0,
        totalVat: 0,
        totalCharges: 0,
        totalDiscounts: 0,
        totalNetValue: 0,
        totalQty: 0,
        documentHeaderExpensesAndCharges: [],
        deliveryDate: undefined,
        companyId: compId,
        companySiteId: '',
        traderSiteId: undefined,
        myDataSyncSuccess: false,
        myDataErrorMessage: '',
        myDataMark: null,
        myDataUuid: '',
        myDataQrCode: '',
      },
    };
  }
}

const isDocumentEditable = computed(() => {
  if (!modifiedSalesDocument.value) return false;
  if (modifiedSalesDocument.value.deleted) return false;
  if (
    modifiedSalesDocument.value.documentHeader.documentStatus?.code !== 'Closed' &&
    !modifiedSalesDocument.value.documentHeader.myDataSyncSuccess
  )
    return true;
  else
    return (
      (!!modifiedSalesDocument.value.documentHeader.documentType?.allowEditClosedDocuments ||
        isAdmin.value ||
        username.value === Users.Admin) &&
      !modifiedSalesDocument.value.documentHeader.myDataSyncSuccess
    );
});

const selectedHeaderTitleTab = ref<string>('details');
const headerTitleTabs = ref([{ name: 'details', label: $t('salesDocuments.tabs.details') }]);

const selectedDetailsTitleTab = ref<string>('materialsTable');
const detailsTitleTabs = ref([
  { name: 'materialsTable', label: $t('salesDocuments.tabs.materials'), link: true },
  { name: 'materialsEnhanced', label: $t('salesDocuments.tabs.materials') },
  { name: 'materialsCard', label: $t('salesDocuments.tabs.materials') },
  { name: 'taxes', label: $t('salesDocuments.tabs.taxes'), link: true },
  { name: 'totals', label: $t('salesDocuments.tabs.totals'), link: true },
  { name: 'remarks', label: $t('salesDocuments.tabs.remarks'), link: true },
]);

const isActiveTab = (tab: { name: string; label: string; link?: boolean }) => {
  return (
    selectedDetailsTitleTab.value === tab.name ||
    (tab.name.includes('materials') && selectedDetailsTitleTab.value.includes('materials'))
  );
};

const confirmModalActive = ref(false);

const isBackMode = ref(false);
const router = useRouter();
function onCancelEdit(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') isBackMode.value = true;
    return;
  } else {
    router.push('/transactions/sales-documents');
  }
  mode.value = 'view';
}

function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  clearDetails.value = true;
  modifiedSalesDocument.value = deepCopy(salesDocument.value);
  if (isBackMode.value) {
    router.push('/transactions/sales-documents');
  }
  clearForm.value = true;
  $v.value.$reset();
}
const $v = useVuelidate();
const toastStore = useToastStore();
const showMissingDetailsModal = ref(false);

async function onSaveChanges() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  if (!docDetailLines.value.filter((x) => x.recordMode !== RecordMode.Deleted).length) {
    showMissingDetailsModal.value = true;
    return;
  }

  if (hasModifiedDetails.value) {
    toastStore.attention({
      text: $t('messages.saveLineChangesPrompt'),
      hideTitle: true,
    });
    return;
  }
  mode.value = 'view';
  if (isModified.value && modifiedSalesDocument.value) {
    updateHeaderTotals();
    updateHeaderDiscounts();

    var save = await addOrEditSalesDocument(modifiedSalesDocument.value);
    if (save.response) {
      isModified.value = false;
      toastStore.success({ text: `${$t('common.actions.save')} ${$t('common.toast.success')}` });
      if (save.error)
        toastStore.error({
          text: `${$t('messages.errors.aadeError')} ${save.error}`,
          timeout: 10000,
        });

      if (
        modifiedSalesDocument.value.documentHeader.documentType?.allowDynamicRange &&
        modifiedSalesDocument.value.customerId &&
        modifiedSalesDocument.value.documentHeader.traderSiteId
      ) {
        await checkAndCreateRangeCustomerTradersite(
          modifiedSalesDocument.value.customerId,
          modifiedSalesDocument.value.documentHeader.traderSiteId
        );
      }

      await loadData();
      if (salesDocument.value)
        modifiedSalesDocument.value = deepCopy<SalesDocument>(salesDocument.value);
    } else {
      toastStore.error({ text: `${$t('common.toast.error')}` });
    }
  }
}

function updateHeaderDiscounts() {
  if (!modifiedSalesDocument.value) return;
  if (!modifiedSalesDocument.value?.documentHeader.documentDetails)
    modifiedSalesDocument.value.documentHeader.documentDetails = [];

  const totalDetailLineDiscounts = modifiedSalesDocument.value.documentHeader.documentDetails
    .filter((x) => x.recordMode !== RecordMode.Deleted)
    .flatMap((x) => x.documentDetailDiscounts)
    .filter((d) => d?.discountOrigin === eDiscountOrigin.Line)
    .reduce((s, d) => s + (d?.discountTotalValue || 0), 0);
  const totalDetailHeaderDiscounts = modifiedSalesDocument.value.documentHeader.documentDetails
    .filter((x) => x.recordMode !== RecordMode.Deleted)
    .flatMap((x) => x.documentDetailDiscounts)
    .filter((d) => d?.discountOrigin === eDiscountOrigin.Header)
    .reduce((s, d) => s + (d?.discountTotalValue || 0), 0);

  if (!modifiedSalesDocument.value.documentHeader.documentHeaderDiscounts)
    modifiedSalesDocument.value.documentHeader.documentHeaderDiscounts = [];

  const headerDiscountLineIndex =
    modifiedSalesDocument.value.documentHeader.documentHeaderDiscounts.findIndex(
      (x) =>
        x.discountOrigin === eDiscountOrigin.Line &&
        x.documentHeaderId === modifiedSalesDocument.value?.documentHeader.id
    );
  if (headerDiscountLineIndex === -1)
    modifiedSalesDocument.value.documentHeader.documentHeaderDiscounts.push({
      id: uuidv4(),
      isFromManager: true,
      documentHeaderId: modifiedSalesDocument.value.documentHeader.id,
      discountOrigin: eDiscountOrigin.Line,
      value: totalDetailLineDiscounts,
      onGrossValue: true,
    });
  else {
    modifiedSalesDocument.value.documentHeader.documentHeaderDiscounts[headerDiscountLineIndex] = {
      ...modifiedSalesDocument.value.documentHeader.documentHeaderDiscounts[
        headerDiscountLineIndex
      ],
      value: totalDetailLineDiscounts,
    };
  }

  const headerDiscountHeaderIndex =
    modifiedSalesDocument.value.documentHeader.documentHeaderDiscounts.findIndex(
      (x) =>
        x.discountOrigin === eDiscountOrigin.Header &&
        x.documentHeaderId === modifiedSalesDocument.value?.documentHeader.id
    );
  if (headerDiscountHeaderIndex === -1)
    modifiedSalesDocument.value.documentHeader.documentHeaderDiscounts.push({
      id: uuidv4(),
      isFromManager: true,
      documentHeaderId: modifiedSalesDocument.value.documentHeader.id,
      discountOrigin: eDiscountOrigin.Header,
      value: totalDetailHeaderDiscounts,
      onGrossValue: true,
    });
  else {
    modifiedSalesDocument.value.documentHeader.documentHeaderDiscounts[headerDiscountHeaderIndex] =
      {
        ...modifiedSalesDocument.value.documentHeader.documentHeaderDiscounts[
          headerDiscountHeaderIndex
        ],
        value: totalDetailHeaderDiscounts,
      };
  }
}

const materialRecentTransactions = ref<string | null>(null);

const showMaterialRecentTransactionsModal = ref(false);
const selectedMaterial = ref<Partial<Material>>();
async function openMaterialInfoModal(payload: { materialId: string; code: string; name: string }) {
  if (!payload) return;
  selectedMaterial.value = payload;
  const resp = await getMaterialRecentTransactions(payload.materialId);
  if (resp) materialRecentTransactions.value = resp.htmlContent;
  showMaterialRecentTransactionsModal.value = true;
}

function openReportTemplate() {
  if (!modifiedSalesDocument.value?.documentHeader.documentType?.defaultReportingTemplateId) {
    toastStore.error({ text: `${$t('messages.errors.noDefaultReportingTemplate')}` });
    return;
  }
  reportPreviewModalOpen.value = true;
}

// Centralized watcher for unit transitions - handles all child components
// Handle unit transitions by reusing material alter code selection logic
async function handleUnitTransition(detail: any, newTotalUnits: number): Promise<any> {
  const clonedDetail = { ...detail };

  // If we have a material alter code, reprocess it with new unit count
  if (clonedDetail.materialAlterCodeId) {
    try {
      // Fetch fresh sibling data
      const siblings = await searchMaterialAlterCodes('', clonedDetail.materialAlterCodeId);

      if (siblings.length > 0 && siblings[0].allMaterialAlterCodes) {
        const processedDetail = processSiblingMaterialAlterCodes(
          clonedDetail,
          siblings[0],
          Math.min(newTotalUnits, 3)
        );
        return processedDetail;
      }
    } catch (error) {
      console.error('Error fetching sibling material alter codes:', error);
    }
  }

  // Fallback to basic initialization if no material alter code or error
  initializeDynamicQuantities(
    clonedDetail,
    modifiedSalesDocument.value?.documentHeader?.documentType
  );
  return clonedDetail;
}

// Centralized watcher for DocumentType changes - updates store so all components get updated
watch(
  () => modifiedSalesDocument.value?.documentHeader?.documentType?.totalMeasurementUnits,
  async (newTotalUnits, oldTotalUnits) => {
    if (
      newTotalUnits !== oldTotalUnits &&
      modifiedSalesDocument.value?.documentHeader?.documentDetails &&
      modifiedSalesDocument.value.documentHeader.documentDetails.length > 0 &&
      mode.value === 'edit'
    ) {
      // Process all details with complex unit transition logic
      const updatedDetails = await Promise.all(
        modifiedSalesDocument.value.documentHeader.documentDetails.map(async (detail) => {
          let transitionedDetail = await handleUnitTransition(detail, newTotalUnits || 1);
          if (!detail.recordMode) transitionedDetail.recordMode = RecordMode.Modified;
          transitionedDetail = priceOrQuantityChange(transitionedDetail, 'quantity');
          saveDocumentLineInStore(transitionedDetail);
          return transitionedDetail;
        })
      );

      if (modifiedSalesDocument.value?.documentHeader)
        modifiedSalesDocument.value.documentHeader.documentDetails = updatedDetails;
    }
  }
);
</script>
